import { orderBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StyledTableV5 } from 'components/DataTable/DataTable.style';

import { type TTurbineProps } from '../../types';

import { getTurbineColumns } from './turbineColumns';

type TurbinesListProps = {
  containerHeight: number;
  data: Array<TTurbineProps>;
};

const TurbinesListComponent = ({ containerHeight, data }: TurbinesListProps) => {
  const { i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (containerRef.current) {
      const parentHeight = containerRef.current.offsetHeight;
      const tableHeader = containerRef.current.querySelector('.ant-table-thead');
      const headerHeight = tableHeader?.clientHeight ?? 0;

      const maxBodyHeight = parentHeight - headerHeight;

      const tableBody = containerRef.current.querySelector('.ant-table-tbody');
      const bodyHeight = tableBody?.clientHeight ?? 0;

      if (bodyHeight > maxBodyHeight) {
        setScrollHeight(maxBodyHeight);
        return;
      }
      setScrollHeight(undefined);
    }
  }, [containerHeight, data]);

  const sortedData = React.useMemo(() => {
    return orderBy(
      data,
      [
        d => d.inspectionData?.maxSeverity ?? -Infinity,
        d => d.inspectionData?.inspectionDate ?? '',
      ],
      ['desc', 'desc']
    );
  }, [data]);

  // i18n.language so a live language switch refreshes titles; React.memo alone would not.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTurbineColumns reads i18n.t at call time
  const columns = React.useMemo(() => getTurbineColumns(), [i18n.language]);

  return (
    <div ref={containerRef} style={{ height: containerHeight }}>
      <StyledTableV5
        columns={columns}
        dataSource={sortedData}
        pagination={false}
        rowKey="key"
        size="middle"
        scroll={scrollHeight ? { y: scrollHeight } : undefined}
        tableLayout="fixed"
      />
    </div>
  );
};

/*
  This table lists every Turbine on the Site without paging, so re-rendering it alongside
  the map is expensive. The Turbines only change when the map data does.
*/
export const TurbinesList = React.memo(TurbinesListComponent);
